import React, { useState, useEffect } from "react";
import Leftbar from "../../components/Leftsidebar";
import Topbar from "../../components/Topbar";
import { Link, useParams } from "react-router-dom";
import "../Project-view/preojectview.css";
import Loaderspinner from "../../components/Loader";
import { ProjectdetailsInfo, usTimezone } from "../Utils/utils";
import { FaFilePdf, FaChevronLeft, FaChevronRight, FaExternalLinkAlt, FaMapMarkerAlt } from "react-icons/fa";
import moment from "moment-timezone";

const formatCurrency = (val) => {
  if (val == null || val === "") return "—";
  return Number(val).toLocaleString("en-US");
};

const formatDate = (val) => {
  if (!val) return "—";
  return moment(val).format("MM/DD/YYYY");
};

const formatLTV = (val) => {
  if (val == null || val === "") return "—";
  return `${Number(val).toFixed(2)}%`;
};

const statusBadge = (status) => {
  const map = {
    funding_requested: { label: "Funding Requested", color: "#e6a817" },
    funding_secured: { label: "Funded", color: "#2dce89" },
    funding_approved: { label: "Funding Approved", color: "#11cdef" },
    funding_cancelled: { label: "Cancelled", color: "#f5365c" },
  };
  const entry = map[status];
  if (!entry) return null;
  return (
    <span className="pv-status-badge" style={{ background: entry.color }}>
      {entry.label}
    </span>
  );
};

const scopeLabel = (scope) => {
  const map = {
    SingleFamily: "Single Family", MultiFamily: "Multi Family",
    Commercial: "Commercial", LandDeal: "Land Deal",
    MobileHome: "Mobile Home", Other: "Other",
  };
  return map[scope] || scope || null;
};

const typeLabel = (type) => {
  const map = {
    FixAndFlip: "Short Term Loan",
    SeekingCapital: "Equity Opportunity",
    BusinessAcquisition: "Business Acquisition",
  };
  return map[type] || type || null;
};

/* ── small sub-components ── */

const InfoRow = ({ label, value }) => (
  <tr>
    <td className="pv-td-label">{label}</td>
    <td className="pv-td-value">{value ?? "—"}</td>
  </tr>
);

const SectionTitle = ({ title }) => (
  <h6 className="pv-section-title">{title}</h6>
);

const MediaSlider = ({ mainImage, additionalImages = [], videoUrl }) => {
  const slides = [];
  if (mainImage) slides.push({ type: "image", src: mainImage });
  (additionalImages || []).forEach((pic) => {
    const url = typeof pic === "string" ? pic : (pic?.image_url || pic?.url || null);
    if (url && typeof url === "string") slides.push({ type: "image", src: url });
  });
  if (videoUrl) slides.push({ type: "video", src: videoUrl });

  const [idx, setIdx] = useState(0);
  if (!slides.length) return null;

  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx((i) => (i + 1) % slides.length);
  const slide = slides[idx];

  return (
    <div className="pv-slider">
      <div className="pv-slider__frame">
        {slide.type === "video" ? (
          <video controls className="pv-slider__img" key={slide.src}>
            <source src={slide.src} type="video/mp4" />
          </video>
        ) : (
          <img src={slide.src} alt="project" className="pv-slider__img" />
        )}
        {slides.length > 1 && (
          <>
            <button className="pv-slider__nav pv-slider__nav--prev" onClick={prev}>
              <FaChevronLeft size={12} />
            </button>
            <button className="pv-slider__nav pv-slider__nav--next" onClick={next}>
              <FaChevronRight size={12} />
            </button>
            <span className="pv-slider__counter">{idx + 1} / {slides.length}</span>
          </>
        )}
      </div>
      {slides.length > 1 && (
        <div className="pv-slider__thumbs">
          {slides.map((s, i) => (
            <div
              key={i}
              className={`pv-slider__thumb${i === idx ? " pv-slider__thumb--active" : ""}`}
              onClick={() => setIdx(i)}
            >
              {s.type === "video"
                ? <div className="pv-slider__thumb-video">▶</div>
                : <img src={s.src} alt="" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Type-specific detail rows ── */

const FixAndFlipRows = ({ p, scope }) => {
  const financialRows = (
    <>
      <InfoRow label="Purchase Price" value={p.purchase_price != null ? `$${formatCurrency(p.purchase_price)} USD` : null} />
      {p.as_is_value != null && <InfoRow label="As-Is Value" value={`$${formatCurrency(p.as_is_value)} USD`} />}
      <InfoRow label="Rehab Costs" value={p.rehab_costs != null ? `$${formatCurrency(p.rehab_costs)} USD` : null} />
      <InfoRow label="Projected After-Repair Value" value={p.after_repair_value != null ? `$${formatCurrency(p.after_repair_value)} USD` : null} />
      <InfoRow label="Capital Needed" value={p.needed != null ? `$${formatCurrency(p.needed)} USD` : null} />
      <InfoRow label="Return for Lender" value={p.return_lender != null ? (p.return_lender_symbol === "percent" ? `${p.return_lender}%` : `$${formatCurrency(p.return_lender)} USD`) : null} />
      <InfoRow label="Loan to Value" value={formatLTV(p.loan_to_value)} />
      <InfoRow label="Lien Position" value={p.position} />
      <InfoRow label="Equity Cross Collateral" value={p.equity_cross_collateral || 'No'} />
      <InfoRow label="Deals Completed (last 2 yrs)" value={p.deals_completed} />
    </>
  );

  if (scope === "MultiFamily") return (
    <>
      <InfoRow label="Years of Experience" value={p.years_experience} />
      <InfoRow label="Number of Units" value={p.units_amount} />
      <InfoRow label="Repairs Done by Current Owner" value={p.repairs_done} />
      {financialRows}
      {p.units_bed_baths?.length > 0 && <UnitsTable rows={p.units_bed_baths} showUnitsCol />}
    </>
  );

  if (scope === "SingleFamily") return (
    <>
      <InfoRow label="Square Footage" value={p.square_footage ? `${formatCurrency(p.square_footage)} ft²` : null} />
      <InfoRow label="Year Built" value={p.years_built} />
      <InfoRow label="Lot Size" value={p.lot_size} />
      {financialRows}
      {p.units_bed_baths?.length > 0 && <UnitsTable rows={p.units_bed_baths} />}
    </>
  );

  if (scope === "Commercial") return (
    <>
      <InfoRow label="Square Footage" value={p.square_footage ? `${formatCurrency(p.square_footage)} ft²` : null} />
      <InfoRow label="Current Use" value={p.current_use} />
      <InfoRow label="Intended New Use" value={p.intended_property} />
      <InfoRow label="Lot Zoned For" value={p.lot_zoned_for} />
      <InfoRow label="Code Violations" value={p.code_violations} />
      <InfoRow label="Encumbrances" value={p.any_encumbrances} />
      <InfoRow label="Licenses / Permits / Certificates" value={p.lincenses_permits_certificates} />
      <InfoRow label="Pending Violations" value={p.pending_violations} />
      <InfoRow label="Lease Agreement" value={p.least_agreement} />
      {financialRows}
    </>
  );

  if (scope === "LandDeal") return (
    <>
      <InfoRow label="Perc Test Done" value={p.perc_test_done} />
      <InfoRow label="Zoned For" value={p.zoning_regulations} />
      <InfoRow label="Historical / City Restrictions" value={p.historical_city_restrictions} />
      <InfoRow label="Septic or City Sewer" value={p.septic_city_sewer} />
      <InfoRow label="Raw Land or Developed" value={p.land_or_developed} />
      <InfoRow label="Utilities Available" value={p.utilities_available} />
      <InfoRow label="Misc (Wooded / Sloped / Graded)" value={p.misc} />
      <InfoRow label="GIS Website Link" value={p.gis_website_link} />
      <InfoRow label="Flood Zone" value={p.flood_zone ? "Yes" : "No"} />
      {financialRows}
    </>
  );

  if (scope === "MobileHome") return (
    <>
      <InfoRow label="Year Built" value={p.years_built} />
      <InfoRow label="Square Footage" value={p.square_footage ? `${formatCurrency(p.square_footage)} ft²` : null} />
      <InfoRow label="Sold with Land or Land Leased" value={p.sold_land_leased} />
      <InfoRow label="Other Info" value={p.other_info} />
      {financialRows}
      {p.units_bed_baths?.length > 0 && <UnitsTable rows={p.units_bed_baths} />}
    </>
  );

  return financialRows;
};

const SeekingCapitalRows = ({ p }) => (
  <>
    <InfoRow label="Purchase Price" value={p.purchase_price != null ? `$${formatCurrency(p.purchase_price)} USD` : null} />
    <InfoRow label="As-Is Value" value={p.as_is_value != null ? `$${formatCurrency(p.as_is_value)} USD` : null} />
    <InfoRow label="Rehab Costs" value={p.rehab_costs != null ? `$${formatCurrency(p.rehab_costs)} USD` : null} />
    <InfoRow label="Projected After-Repair Value" value={p.projected_after_repair != null ? `$${formatCurrency(p.projected_after_repair)} USD` : null} />
    <InfoRow label="Capital Needed" value={p.needed != null ? `$${formatCurrency(p.needed)} USD` : null} />
    <InfoRow label="Equity % for Funder" value={p.equity_percentage_lender != null ? `${p.equity_percentage_lender}%` : null} />
    <InfoRow label="Monetization Strategy" value={p.monetization_strategy} />
    <InfoRow label="Profit Split & Payment" value={p.profit_split_and_payment} />
    <InfoRow label="Deals Completed (last 2 yrs)" value={p.deals_completed} />
  </>
);

const BusinessAcquisitionRows = ({ p }) => (
  <>
    <InfoRow label="Year Established" value={p.year_stablished_age} />
    <InfoRow label="Business Trade" value={p.business_trade} />
    <InfoRow label="EBITDA" value={p.EBITDA} />
    <InfoRow label="Net Income After Expenses" value={p.net_income != null ? `$${formatCurrency(p.net_income)} USD` : null} />
    <InfoRow label="Who Will Be the Operator" value={p.operator} />
    <InfoRow label="Plan to Increase Revenue / Modernize" value={p.increase_renevue} />
    <InfoRow label="Broad Overview & Negotiated Agreements" value={p.broad_overview} />
    <InfoRow label="Other Info" value={p.other_info} />
  </>
);

const UnitsTable = ({ rows, showUnitsCol }) => (
  <tr>
    <td colSpan={2} style={{ paddingTop: 10, paddingBottom: 10 }}>
      <table className="pv-units-table">
        <thead>
          <tr>
            {showUnitsCol && <th>Units</th>}
            <th>Beds</th>
            <th>Baths</th>
            {showUnitsCol && <th>Rent</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((u, i) => (
            <tr key={i}>
              {showUnitsCol && <td>{u.units ?? "—"}</td>}
              <td>{u.beds ?? "—"}</td>
              <td>{u.baths ?? "—"}</td>
              {showUnitsCol && <td>{u.rent ?? "—"}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </td>
  </tr>
);

const DocumentsSection = ({ p }) => {
  const docs = [
    { label: "Purchase Agreement", url: p.purchase_agreement },
    { label: "Contractor Bid", url: p.contractor_bid },
    { label: "Appraisal", url: p.appraisal },
    { label: "Inspection Report", url: p.inspection_report },
    { label: "Operating Agreement", url: p.operating_agreement },
    { label: "Offering Memorandum", url: p.offering_memorandum },
    { label: "Free Credit Report", url: p.free_credit_report },
    { label: "T12 / T24", url: p.T12_T24 },
    { label: "Rent Rolls", url: p.rent_rolls_lender },
    { label: "Permits / Approvals", url: p.permits_approvals },
    { label: "Environmental Assessment", url: p.enviromental_assessment },
    { label: "Income / Expense Sheet", url: p.income_expense_sheet },
    { label: "Perc Test File", url: p.perc_test_file },
    { label: "Certificate of Standing", url: p.certificate_standing_llc },
    { label: "Letter of Intent", url: p.accepted_letter_intent },
    { label: "Gov ID", url: p.gov_id },
    { label: "Business P&L", url: p.business_pl },
    { label: "Other Files", url: p.project_other_files },
  ].filter((d) => d.url);

  if (!docs.length) return null;

  return (
    <>
      <SectionTitle title="Documents" />
      <div className="pv-docs-row">
        {docs.map((d) => (
          <a key={d.label} href={d.url} target="_blank" rel="noopener noreferrer" className="pv-doc-chip">
            <FaFilePdf className="pv-doc-chip__icon" />
            {d.label}
          </a>
        ))}
      </div>
    </>
  );
};

/* ── Main page ── */

const Projectview = () => {
  const { id } = useParams();
  const [projectInfo, setProjectInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  const handleSidebar = () => setShowSidebar(!showSidebar);
  const handleSidebarClose = () => setShowSidebar(false);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          setLoading(true);
          const resp = await ProjectdetailsInfo(id);
          setProjectInfo(resp?.data || {});
        } catch (e) {
          console.log(e);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id]);

  const p = projectInfo;
  const type = p.type;
  const scope = p.project_scope || p.capital_partner;

  const exitLabel = (val) => {
    if (val === "refinance") return "Refinance";
    if (val === "fix_flip") return "Fix and Flip";
    if (val === "other") return p.exit_strategy_other || "Other";
    return val || "—";
  };

  const hasLinks = Array.isArray(p.link_to_website) &&
    p.link_to_website.some((l) => (typeof l === "object" ? l?.value : l));

  return (
    <>
      {loading && <Loaderspinner />}
      <div className="g-sidenav-show bg-gray-100">
        <div id="wrapper">
          <Leftbar showSidebar={showSidebar} handleSidebarClose={handleSidebarClose} />
          <main className="main-content border-radius-lg">
            <Topbar handleSidebar={handleSidebar} />

            {/* ── teal header band (same as every other page) ── */}
            <div className="header header-bg pb-6">
              <div className="container-fluid">
                <div className="header-body">
                  <div className="row align-items-center py-4">
                    <div className="col-lg-6 col-7">
                      <nav aria-label="breadcrumb" className="d-none d-md-inline-block ml-md-4">
                        <ol className="breadcrumb breadcrumb-links breadcrumb-dark" style={{ background: "transparent" }}>
                          <li className="breadcrumb-item">
                            <Link to="/dashboard"><i className="fas fa-home" /></Link>
                          </li>
                          <li className="breadcrumb-item mt-2">
                            <Link to="/projects" style={{ textDecoration: "none", fontSize: "15px" }}>Projects</Link>
                          </li>
                          <li className="breadcrumb-item mt-2">
                            <span style={{ color: "#f6f9fc" }}>View Project</span>
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── single white card, same as every other page ── */}
            <div className="container-fluid mt--6">
              <div className="row">
                <div className="col">
                  <div className="card mb-4">
                    <div className="card-body">

                      {/* ── 1. Header row: image + key info side by side ── */}
                      <div className="pv-header">
                        <div className="pv-header__media">
                          <MediaSlider
                            mainImage={p.project_picture}
                            additionalImages={p.project_pictures || []}
                            videoUrl={p.project_video}
                          />
                        </div>

                        <div className="pv-header__info">
                          {/* title + badges */}
                          <h5 className="pv-project-name">{p.project_name || "—"}</h5>
                          <div className="pv-badges">
                            {typeLabel(type) && <span className="pv-badge pv-badge--type">{typeLabel(type)}</span>}
                            {scopeLabel(scope) && <span className="pv-badge pv-badge--scope">{scopeLabel(scope)}</span>}
                            {statusBadge(p.status)}
                          </div>

                          {/* location */}
                          {(p.state || p.city || p.country) && (
                            <p className="pv-location">
                              <FaMapMarkerAlt className="pv-location__icon" />
                              {[p.city, p.state, p.country].filter(Boolean).join(", ")}
                            </p>
                          )}

                          {/* quick stat pills */}
                          <div className="pv-stats">
                            <div className="pv-stat">
                              <span className="pv-stat__val">{p.project_views ?? 0}</span>
                              <span className="pv-stat__lbl">Views</span>
                            </div>
                            <div className="pv-stat">
                              <span className="pv-stat__val">{p.interest_request_count ?? 0}</span>
                              <span className="pv-stat__lbl">Interests</span>
                            </div>
                            {p.by_date && (
                              <div className="pv-stat">
                                <span className="pv-stat__val">{formatDate(p.by_date)}</span>
                                <span className="pv-stat__lbl">Due Date</span>
                              </div>
                            )}
                            {(p.payback_date || p.by_date) && (
                              <div className="pv-stat">
                                <span className="pv-stat__val">{formatDate(p.payback_date || p.by_date)}</span>
                                <span className="pv-stat__lbl">{type === "SeekingCapital" ? "Est. Distribution" : "Payback Date"}</span>
                              </div>
                            )}
                            {p.created_at && (
                              <div className="pv-stat">
                                <span className="pv-stat__val">{formatDate(p.created_at)}</span>
                                <span className="pv-stat__lbl">Created</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* overview */}
                      {p.overview && (
                        <div className="pv-overview">
                          <span className="pv-overview__label">Overview</span>
                          <p className="pv-overview__text">{p.overview}</p>
                        </div>
                      )}

                      <hr />

                      {/* ── 2. Info table ── */}
                      <div className="row">
                        {/* left column */}
                        <div className="col-lg-6">

                          {/* borrower */}
                          {p.user && (
                            <>
                              <SectionTitle title="Borrower" />
                              <div className="pv-borrower">
                                {p.user.about?.photo
                                  ? <img src={p.user.about.photo} alt="borrower" className="pv-borrower__avatar" />
                                  : <div className="pv-borrower__avatar pv-borrower__avatar--init">
                                    {(p.user.about?.username || "?")[0].toUpperCase()}
                                  </div>
                                }
                                <div>
                                  <div className="pv-borrower__name">{p.user.about?.username || "—"}</div>
                                  <div className="pv-borrower__sub">
                                    {p.user.about?.email && <span>{p.user.about.email}</span>}
                                    {p.user.about?.idenfyStatus === "APPROVED"
                                      ? <span className="pv-chip pv-chip--green">Verified ✓</span>
                                      : <span className="pv-chip pv-chip--orange">Not Verified</span>
                                    }
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {/* funding info */}
                          {p.funder && (
                            <>
                              <SectionTitle title="Funding" />
                              <table className="pv-table">
                                <tbody>
                                  {p.status === "funding_secured" && p.funded && (
                                    <InfoRow label="Funded By" value={p.funder?.about?.username} />
                                  )}
                                  {(p.status === "funding_requested" || p.status === "funding_approved") && (
                                    <InfoRow label="Funding Offered By" value={p.funder?.about?.username} />
                                  )}
                                </tbody>
                              </table>
                            </>
                          )}

                          {/* project details */}
                          <SectionTitle title="Project Details" />
                          <table className="pv-table">
                            <tbody>
                              {type === "FixAndFlip" && <FixAndFlipRows p={p} scope={scope} />}
                              {type === "SeekingCapital" && <SeekingCapitalRows p={p} />}
                              {type === "BusinessAcquisition" && <BusinessAcquisitionRows p={p} />}
                            </tbody>
                          </table>
                        </div>

                        {/* right column */}
                        <div className="col-lg-6">

                          {/* exit strategy (not for BA) */}
                          {type !== "BusinessAcquisition" && (
                            <>
                              <SectionTitle title="Exit Strategy" />
                              <table className="pv-table">
                                <tbody>
                                  <InfoRow label="Strategy" value={exitLabel(p.exit_strategy)} />
                                  {p.cross_collateral_offered && (
                                    <InfoRow label="Cross Collateral Offered" value={p.cross_collateral_offered} />
                                  )}
                                  {p.property_address && (
                                    <>
                                      <InfoRow label="Property Address" value={p.property_address} />
                                      <InfoRow label="Market Value" value={p.current_market_value != null ? `$${formatCurrency(p.current_market_value)}` : null} />
                                      <InfoRow label="Total Debt" value={p.total_debt_on_property != null ? `$${formatCurrency(p.total_debt_on_property)}` : null} />
                                      <InfoRow label="Ownership %" value={p.your_ownership_percentage} />
                                      <InfoRow label="Available Equity" value={p.available_equity != null ? `$${formatCurrency(p.available_equity)}` : null} />
                                    </>
                                  )}
                                </tbody>
                              </table>
                            </>
                          )}

                          {/* links */}
                          {hasLinks && (
                            <>
                              <SectionTitle title="Links" />
                              <div className="pv-links">
                                {(p.link_to_website || []).map((link, i) => {
                                  const url = typeof link === "object" ? link?.value : link;
                                  if (!url) return null;
                                  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
                                  return (
                                    <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="pv-link">
                                      <FaExternalLinkAlt size={11} /> {url}
                                    </a>
                                  );
                                })}
                              </div>
                            </>
                          )}

                          {/* documents */}
                          <DocumentsSection p={p} />

                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>

          </main>
        </div>
      </div>
    </>
  );
};

export default Projectview;
